import { Button, formatPhone, Modal, phoneDigits, useToast } from '@cartwheel/components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CallEndedState, QualityAlert } from '../hooks/use-twilio-device'
import { useTwilioDevice } from '../hooks/use-twilio-device'
import { extractApiErrorDetails } from '../utils/api-error-details'

const KEYPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
] as const

export interface BrowserCallModalProps {
  isOpen: boolean
  onClose: () => void
  targetNumber?: string | null
  targetLabel?: string | null
  clientId?: string | null
  purpose?: string | null
}

interface BrowserTokenBody {
  target_number: string
  client_id?: string
  target_label?: string
  purpose?: string
}

interface BrowserTokenResponse {
  id: string
  token: string
}

async function fetchBrowserToken(body: BrowserTokenBody): Promise<BrowserTokenResponse> {
  const csrfMeta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  const response = await fetch('/api/v1/calls/browser_token', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(csrfMeta ? { 'X-CSRF-Token': csrfMeta.content } : {}),
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as unknown
    throw error
  }

  return response.json() as Promise<BrowserTokenResponse>
}

export default function BrowserCallModal({
  isOpen,
  onClose,
  targetNumber,
  targetLabel,
  clientId,
  purpose,
}: BrowserCallModalProps) {
  const toast = useToast()
  const {
    step,
    isMuted,
    ended,
    isStarting,
    qualityAlert,
    startCall,
    mute,
    hangUp,
    sendDigit,
    reset,
  } = useTwilioDevice()

  const [manualTarget, setManualTarget] = useState('')
  const [manualLabel, setManualLabel] = useState('')

  const resolvedNumber = useMemo(
    () =>
      targetNumber ??
      (phoneDigits(manualTarget).length >= 10 ? phoneDigits(manualTarget) : undefined),
    [targetNumber, manualTarget]
  )

  const resetModal = useCallback(() => {
    setManualTarget('')
    setManualLabel('')
  }, [])

  const handleClose = useCallback(() => {
    reset()
    resetModal()
    onClose()
  }, [reset, resetModal, onClose])

  useEffect(() => {
    if (!isOpen) {
      reset()
      resetModal()
    }
  }, [isOpen, reset, resetModal])

  const handleStartCall = async () => {
    if (!resolvedNumber) return

    let token: string
    let callLogId: string

    try {
      const result = await fetchBrowserToken({
        target_number: resolvedNumber,
        client_id: clientId ?? undefined,
        target_label: targetLabel ?? manualLabel ?? undefined,
        purpose: purpose ?? undefined,
      })
      token = result.token
      callLogId = result.id
    } catch (err) {
      const message = extractApiErrorDetails(err) ?? 'Could not start the call. Please try again.'
      toast.error({ description: message })
      return
    }

    await startCall(token, callLogId, resolvedNumber)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Call in browser">
      {step === 'confirm' && (
        <ConfirmStep
          targetLabel={targetLabel ?? undefined}
          targetNumber={targetNumber ?? undefined}
          manualTarget={manualTarget}
          setManualTarget={setManualTarget}
          manualLabel={manualLabel}
          setManualLabel={setManualLabel}
          resolvedNumber={resolvedNumber}
          isStarting={isStarting}
          onClose={handleClose}
          onConfirm={handleStartCall}
        />
      )}
      {(step === 'connecting' || step === 'ringing') && (
        <ProgressStep
          phase={step}
          targetLabel={targetLabel ?? (manualLabel || undefined)}
          targetNumber={targetNumber ?? (manualTarget || undefined)}
          qualityAlert={qualityAlert}
          onHangUp={hangUp}
        />
      )}
      {step === 'in_call' && (
        <InCallStep
          targetLabel={targetLabel ?? (manualLabel || targetNumber) ?? (manualTarget || undefined)}
          isMuted={isMuted}
          qualityAlert={qualityAlert}
          onMute={mute}
          onHangUp={hangUp}
          onSendDigit={sendDigit}
        />
      )}
      {step === 'ended' && <EndedStep ended={ended} onClose={handleClose} />}
    </Modal>
  )
}

function ConfirmStep({
  targetLabel,
  targetNumber,
  manualTarget,
  setManualTarget,
  manualLabel,
  setManualLabel,
  resolvedNumber,
  isStarting,
  onClose,
  onConfirm,
}: {
  targetLabel?: string
  targetNumber?: string
  manualTarget: string
  setManualTarget: (v: string) => void
  manualLabel: string
  setManualLabel: (v: string) => void
  resolvedNumber?: string
  isStarting: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-charcoal-75">
        You&apos;ll connect directly from this browser tab using your microphone — no phone
        required. The call will show the Cartwheel office number to{' '}
        <strong>{targetLabel ?? targetNumber ?? 'the recipient'}</strong>.
      </p>

      {!targetNumber && (
        <div className="space-y-3">
          <input
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder="(555) 123-4567"
            value={formatPhone(manualTarget)}
            onChange={(e) => setManualTarget(e.target.value)}
            className="bg-white border border-gray-stroke h-10 w-full rounded-lg px-4 text-base outline-none transition-colors focus:ring-0 focus:border-lavender"
          />
          <input
            type="text"
            placeholder="Recipient label (e.g. 'School counselor')"
            value={manualLabel}
            onChange={(e) => setManualLabel(e.target.value)}
            className="bg-white border border-gray-stroke h-10 w-full rounded-lg px-4 text-base outline-none transition-colors focus:ring-0 focus:border-lavender"
          />
        </div>
      )}

      {targetNumber && (
        <div className="rounded-lg border border-gray-stroke bg-sand-25 p-4 space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-charcoal-50">Calling</p>
          <p className="text-base font-semibold text-charcoal">{formatPhone(targetNumber)}</p>
          {targetLabel && <p className="text-xs text-charcoal-50">{targetLabel}</p>}
        </div>
      )}

      <div className="rounded-lg border border-gray-stroke bg-sand-25 p-4 flex items-start gap-3">
        <i className="fa-solid fa-microphone text-charcoal-50 mt-0.5 shrink-0" aria-hidden="true" />
        <p className="text-sm text-charcoal-75">
          Your browser will ask for microphone permission when you start the call.
        </p>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={isStarting || !resolvedNumber}>
          {isStarting ? 'Starting…' : 'Call in browser'}
        </Button>
      </div>
    </div>
  )
}

function QualityAlertBanner({ alert }: { alert: QualityAlert }) {
  const isNoAudio = alert.severity === 'no_audio'
  return (
    <div
      className={`rounded-lg border p-4 flex items-start gap-3 ${
        isNoAudio ? 'border-brick-25 bg-brick-10' : 'border-gray-stroke bg-sand-25'
      }`}
    >
      <i
        className={`fa-solid fa-triangle-exclamation mt-0.5 shrink-0 ${
          isNoAudio ? 'text-brick' : 'text-charcoal-50'
        }`}
        aria-hidden="true"
      />
      <p className="text-sm text-charcoal-75">{alert.message}</p>
    </div>
  )
}

function ProgressStep({
  phase,
  targetLabel,
  targetNumber,
  qualityAlert,
  onHangUp,
}: {
  phase: 'connecting' | 'ringing'
  targetLabel?: string
  targetNumber?: string
  qualityAlert: QualityAlert | null
  onHangUp: () => void
}) {
  const display = targetLabel || (targetNumber ? formatPhone(targetNumber) : 'recipient')
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-stroke bg-sand-25 p-4 flex items-center gap-3">
        <i
          className={`fa-solid ${phase === 'ringing' ? 'fa-phone-volume text-charcoal-50' : 'fa-spinner fa-spin text-charcoal-50'}`}
          aria-hidden="true"
        />
        <div>
          <p className="text-sm text-charcoal-75">
            {phase === 'ringing' ? 'Ringing' : 'Connecting to'} <strong>{display}</strong>…
          </p>
          {phase === 'ringing' && (
            <p className="text-xs text-charcoal-50">
              You should hear ringing until they pick up. Nobody has answered yet.
            </p>
          )}
        </div>
      </div>
      {qualityAlert && <QualityAlertBanner alert={qualityAlert} />}
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onHangUp}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function InCallStep({
  targetLabel,
  isMuted,
  qualityAlert,
  onMute,
  onHangUp,
  onSendDigit,
}: {
  targetLabel?: string
  isMuted: boolean
  qualityAlert: QualityAlert | null
  onMute: () => void
  onHangUp: () => void
  onSendDigit: (digit: string) => void
}) {
  const [digitDisplay, setDigitDisplay] = useState('')

  const handleDigit = (digit: string) => {
    onSendDigit(digit)
    setDigitDisplay((prev) => prev + digit)
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-stroke bg-mint-25 p-4 flex items-center gap-3">
        <i className="fa-solid fa-phone text-forest" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-charcoal">On a call</p>
          <p className="text-xs text-charcoal-75">
            Connected to <strong>{targetLabel ?? 'recipient'}</strong>
          </p>
        </div>
      </div>

      {qualityAlert && <QualityAlertBanner alert={qualityAlert} />}

      <div className="rounded-lg border border-gray-stroke bg-sand-25 px-4 py-2 flex items-center gap-3">
        <i className="fa-solid fa-keyboard text-charcoal-50 text-xs shrink-0" aria-hidden="true" />
        <span className="text-xs font-medium text-charcoal-50 uppercase tracking-wide shrink-0">
          Ext
        </span>
        <p className="text-sm font-mono text-charcoal tracking-widest flex-1 min-w-0 truncate">
          {digitDisplay || <span className="text-charcoal-25">—</span>}
        </p>
      </div>

      <div className="rounded-lg border border-gray-stroke bg-sand-25 p-3">
        <div className="grid grid-cols-3 gap-2">
          {KEYPAD_KEYS.flat().map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => handleDigit(key)}
              className="h-10 rounded-lg border border-gray-stroke bg-white text-sm font-semibold text-charcoal hover:bg-sand-25 active:bg-sand-50 transition-colors"
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button variant="secondary" onClick={onMute}>
          <i
            className={`fa-solid ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'} mr-2`}
            aria-hidden="true"
          />
          {isMuted ? 'Unmute' : 'Mute'}
        </Button>
        <Button onClick={onHangUp} className="bg-brick text-white hover:bg-brick/90">
          <i className="fa-solid fa-phone-slash mr-2" aria-hidden="true" />
          Hang up
        </Button>
      </div>
    </div>
  )
}

function endedTitle(reason: CallEndedState['reason'] | undefined): string {
  switch (reason) {
    case 'completed':
      return 'Call ended'
    case 'canceled':
      return 'Call canceled'
    case 'no_answer':
      return 'No answer'
    default:
      return 'Call failed'
  }
}

function EndedStep({ ended, onClose }: { ended: CallEndedState | null; onClose: () => void }) {
  const isError = ended?.reason === 'failed'
  const isNoAnswer = ended?.reason === 'no_answer'

  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg border p-4 flex items-start gap-3 ${
          isError ? 'border-brick-25 bg-brick-10' : 'border-gray-stroke bg-sand-25'
        }`}
      >
        <i
          className={`fa-solid ${
            isError
              ? 'fa-circle-exclamation text-brick'
              : isNoAnswer
                ? 'fa-phone-slash text-charcoal-50'
                : 'fa-circle-check text-forest'
          } mt-0.5 shrink-0`}
          aria-hidden="true"
        />
        <div>
          <p className="text-sm font-medium text-charcoal">{endedTitle(ended?.reason)}</p>
          {isNoAnswer && <p className="text-xs text-charcoal-50 mt-1">They didn&apos;t pick up.</p>}
          {ended?.errorMessage && <p className="text-xs text-brick mt-1">{ended.errorMessage}</p>}
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  )
}
